<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge
                            v-if="filtersCount > 0"
                            data-test="reset"
                            :borders="false"
                            size="xs"
                            @click.stop="reset"
                        >
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>
                                <vx-input
                                    type="text"
                                    data-test="query"
                                    :placeholder="props.placeholder"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button
                                    data-test="cancel"
                                    type="button"
                                    size="lg"
                                    color="muted-light"
                                    @click="cancel"
                                >
                                    Cancel
                                </vx-button>
                                <vx-button data-test="apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group v-if="props.sortOptions.length" size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="props.sortOptions"
                    ></vx-select>
                    <vx-button
                        @click.prevent="toggleSortDir"
                        type="button"
                        color="white"
                        shadow
                        :aria-label="`Sort ${
                            props.modelValue.sort_dir === 'asc' ? 'ascending' : 'descending'
                        }. Press to toggle sort direction.`"
                        data-test="sort-direction"
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
                <vx-button
                    v-else
                    @click.prevent="toggleSortDir"
                    type="button"
                    color="white"
                    size="lg"
                    shadow
                    :aria-label="`Sort ${
                        props.modelValue.sort_dir === 'asc' ? 'ascending' : 'descending'
                    }. Press to toggle sort direction.`"
                    data-test="sort-direction"
                >
                    <font-awesome-icon
                        widthAuto
                        :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                    ></font-awesome-icon>
                </vx-button>
            </div>
        </template>
        <template v-slot:right>
            <vx-button
                @click="emit('export')"
                type="button"
                color="white"
                size="lg"
                shadow
                :loading="props.exporting"
                data-test="export"
            >
                Export CSV <font-awesome-icon widthAuto :icon="faFileCsv" />
            </vx-button>
        </template>
    </page-controls>
</template>

<script lang="ts" setup>
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faCircleXmark,
    faFileCsv,
    faMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxButton, VxButtonGroup, VxFilterable, VxInput, VxSelect } from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import PageControls from '~/components/general/PageControls.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';
import { filtersKey } from '~/composables/useCursorPagination';
import type { TcrListParams } from '~/services/adminTenDlcClient.types';

const props = withDefaults(
    defineProps<{
        modelValue: TcrListParams;
        placeholder?: string;
        sortOptions?: { label: string; value: NonNullable<TcrListParams['sort_field']> }[];
        exporting?: boolean;
    }>(),
    {
        placeholder: 'Search',
        sortOptions: () => [],
        exporting: false,
    }
);

const emit = defineEmits<{
    'update:modelValue': [value: TcrListParams];
    apply: [];
    reset: [];
    export: [];
}>();

const filters = ref<TcrListParams>({ ...props.modelValue });

watch(
    () => filtersKey(props.modelValue),
    () => {
        filters.value = { ...props.modelValue };
    },
    { immediate: true }
);

const active = ref(false);

const toggleFilterable = (event: boolean) => {
    active.value = event;
};

const nonFilterKeys = ['cursor', 'per_page', 'sort_dir', 'sort_field'];

const filtersCount = computed(
    () =>
        Object.entries(props.modelValue).filter(
            ([key, value]) => !nonFilterKeys.includes(key) && undefined !== value && '' !== value
        ).length
);

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};

const toggleSortDir = () => {
    filters.value.sort_dir = filters.value.sort_dir === 'asc' ? 'desc' : 'asc';
    apply();
};
</script>
